import React from 'react';
import { useTranslation } from 'react-i18next';
import { FiChevronDown, FiSearch } from 'react-icons/fi';
import Swal from 'sweetalert2';

const DispatchAssignmentPanel = ({
  availableDriversCount,
  formData,
  handleInputChange,
  selectedType,
  selectedShipping,
  setSelectedShipping,
  setSelectedType,
  setFormData,
  selectedContainer,
  setSelectedContainer,
  handleContainerSelect,
  selectedContainers,
  setSelectedContainers,
  handleContainersSelect,
  setSelectedContainerRaw,
  setSelectedContainersRaw,
  shipments,
  isLoading,
  selectedDriver,
  setSelectedDriver,
  showDropdowns,
  setShowDropdowns,
  search,
  setSearch,
  filteredDrivers,
  distance,
  setDistance,
  handleTypeSelect,
  setShowExportModal,
  setShowImportModal,
  setShowLTLModal,
  setShowFTLModal,
}) => {
  const { t } = useTranslation();

  return (
    <div className="bg-[#1D2461] mt-4 text-white p-6 rounded-xl mx-auto flex flex-col md:flex-row md:items-start gap-6">
      {/* Left Panel */}
      <div className="w-full md:w-1/2 flex flex-col items-center justify-center text-center">
        <div className="text-start">
          <h2 className="text-2xl font-semibold">{t('dashboard.dispatchAssignment')}</h2>
          <p className="text-base text-gray-400 mb-4">{t('dashboard.autoAssignDescription')}</p>
        </div>
        <div
          className="w-full md:w-2/3 lg:w-1/2 h-[110px] rounded-md flex flex-col justify-center items-center mb-4"
          style={{ background: 'linear-gradient(167.8deg, #4100B4 0.5%, #1C004E 99.5%)' }}
        >
          <div className="relative flex items-center justify-center">
            <div className="absolute w-[60px] h-[20px] rounded-full bg-[#00f0ff] blur-[30px] opacity-90"></div>
            <div className="text-4xl font-inter font-bold text-white z-10">{availableDriversCount}</div>
          </div>
          <span className="text-xl">{t('dashboard.availableDrivers')}</span>
        </div>
        <span className="text-xl mb-1">{t('dashboard.automaticAssignment')}</span>
      </div>

      {/* Divider */}
      <div className="hidden md:block border-l-2 border-dotted border-white/40 h-auto min-h-[200px] self-stretch"></div>

      {/* Right Panel */}
      <div className="w-full md:w-1/2 grid gap-4 px-4">
        {/* Dispatch Mode Select */}
        {(selectedType === 'LTL' || selectedType === 'FTL' || selectedType === 'Export' || selectedType === 'Import') && (
          <div>
            <label className="block text-lg mb-1">{t('orders.selectDispatchMode')}</label>
            <select
              name="dispatchMode"
              value={formData[selectedType.toLowerCase()].dispatchMode}
              onChange={(e) => handleInputChange(e, null, selectedType)}
              className="w-full px-3 py-2 rounded bg-white text-black"
              aria-label={t('orders.selectDispatchMode')}
            >
              <option value="">{t('orders.selectDispatchModePlaceholder')}</option>
              <option value="Pick-up Load">{t('orders.pickUpLoad')}</option>
              <option value="Delivery">{t('orders.delivery')}</option>
            </select>
          </div>
        )}

        {/* Container Select */}
        <select
          className="w-full px-3 py-2 rounded text-black"
          multiple={selectedType === 'Export'}
          value={selectedType === 'Export' ? selectedContainers : selectedContainer}
          onChange={(e) => {
            if (selectedType === 'Export') {
              const selectedOptions = Array.from(e.target.selectedOptions, option => option.value);
              setSelectedContainers(selectedOptions);
              handleContainersSelect(selectedOptions);
              const rawList = shipments.filter(s =>
                selectedOptions.includes(String(s.containerNumber).trim())
              );
              setSelectedContainersRaw(rawList);
            } else {
              const value = e.target.value;
              setSelectedContainer(value);
              handleContainerSelect(value);
              const rawData = shipments.find(s =>
                String(s.containerNumber).trim() === String(value).trim()
              );
              setSelectedContainerRaw(rawData || null);
            }
          }}
          disabled={isLoading}
          aria-label={t('alt.selectContainer')}
        >
          <option value="">{t('orders.selectContainerPlaceholder')}</option>
          {shipments.map((shipment) => (
            <option key={shipment._id} value={shipment.containerNumber}>
              {shipment.containerNumber || `CNT-${shipment._id.slice(-6)}`}
            </option>
          ))}
        </select>

        {/* Driver & Distance */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Driver Dropdown */}
          <div className="relative">
            <label className="text-lg block mb-1">{t('orders.selectDriver')}</label>
            <div
              className="w-full px-3 py-2 rounded bg-white text-black flex justify-between items-center cursor-pointer"
              onClick={() => setShowDropdowns(!showDropdowns)}
              aria-expanded={showDropdowns}
              aria-label={t('alt.selectDriver')}
            >
              {selectedDriver ? (
                <div className="flex items-center gap-2">
                  {selectedDriver.image && (
                    <img src={selectedDriver.image} alt={t('alt.driverAvatar')} className="w-6 h-6 rounded-full" />
                  )}
                  <span>{selectedDriver.fullName}</span>
                </div>
              ) : (
                <span className="text-gray-400">{t('Select Driver Name or ID')}</span>
              )}
              <FiChevronDown />
            </div>

            {showDropdowns && (
              <div className="absolute z-10 mt-1 w-full bg-white shadow-lg rounded p-3">
                <div className="relative mb-2">
                  <input
                    type="text"
                    placeholder={t('orders.searchDriverPlaceholder')}
                    className="w-full px-4 py-2 bg-violet-100 text-black rounded"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                  />
                  <FiSearch className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500" />
                </div>
                <div className="max-h-48 overflow-y-auto">
                  {filteredDrivers.map((driver) => (
                    <div
                      key={driver._id}
                      className="flex items-center gap-2 p-2 hover:bg-gray-100 rounded cursor-pointer"
                      onClick={() => {
                        setSelectedDriver(driver);
                        setShowDropdowns(false);
                      }}
                    >
                      {driver.image && (
                        <img src={driver.image} alt={t('alt.driverAvatar')} className="w-6 h-6 rounded-full" />
                      )}
                      <span className="text-black">{driver.fullName}</span>
                    </div>
                  ))}
                  {filteredDrivers.length === 0 && (
                    <div className="text-gray-500 text-sm text-center py-2">{t('orders.noDriverFound')}</div>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Distance */}
          <div>
            <label className="text-lg block mb-1">{t('orders.distance')}</label>
            <input
              type="number"
              placeholder="Enter distance (km)"
              className="w-full px-3 py-2 rounded bg-white text-black border focus:outline-none focus:ring-2 focus:ring-violet-500"
              value={distance}
              onChange={(e) => {
                const value = e.target.value;
                if (value === '' || Number(value) >= 0) setDistance(value);
              }}
            />
          </div>
        </div>

        {/* Shipping Method Buttons */}
        <div>
          <label className="block text-lg mb-1">{t('orders.selectShippingMethod')}</label>
          <div className="flex gap-2">
            {['LTL', 'FTL'].map((method) => (
              <button
                key={method}
                className={`px-3 py-2 rounded text-sm w-full ${selectedShipping === method ? 'bg-white text-black border-4 border-[#4880ff]' : 'bg-white text-black'}`}
                onClick={() => {
                  setSelectedShipping(method);
                  setSelectedType(method);
                  setFormData((prev) => ({ ...prev, [method.toLowerCase()]: { ...prev[method.toLowerCase()], shippingMethod: method } }));
                }}
                aria-label={t(`alt.select${method}`)}
              >
                {t(`orders.${method}`)}
              </button>
            ))}
          </div>
        </div>

        {/* Port Dispatch Buttons */}
        <div>
          <label className="block text-lg mb-1">{t('orders.selectPortDispatch')}</label>
          <div className="flex gap-2">
            {['Export', 'Import'].map((type) => (
              <button
                key={type}
                className={`px-4 py-2 rounded text-sm w-full ${selectedType === type ? 'bg-white text-black border-4 border-[#4880ff]' : 'bg-white text-black'}`}
                onClick={() => handleTypeSelect(type)}
                aria-label={t(`alt.select${type}`)}
              >
                {t(`orders.${type.toLowerCase()}`)}
              </button>
            ))}
          </div>
        </div>

        {/* Proceed Button */}
        <div className="text-center">
          <button
            onClick={() => {
              if (selectedType === 'Export') setShowExportModal(true);
              else if (selectedType === 'Import') {
                if (!selectedContainer) {
                  Swal.fire(t('errors.error'), t('errors.selectContainer'), 'info');
                  return;
                }
                setShowImportModal(true);
              } else if (selectedType === 'LTL') setShowLTLModal(true);
              else if (selectedType === 'FTL') setShowFTLModal(true);
              else Swal.fire(t('errors.error'), t('errors.selectDispatchType'), 'info');
            }}
            className="bg-[#792aff] w-full max-w-[271px] text-white font-semibold rounded-md py-2 mt-2"
            disabled={isLoading}
            aria-label={t('alt.proceed')}
          >
            {isLoading ? t('dashboard.loading') : t('orders.next')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default DispatchAssignmentPanel;